import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button, Display, Subtitle1, makeStyles } from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import { useQuery } from '@apollo/client';
import {
  bodyContentContainer,
  iconButtonStyles,
  truncatedLinkStyle,
} from '../../../app/common/styles/CommonStyleObjects';
import Card from '../../common/card/Card';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import CanAccess from '../../common/canAccess/CanAccess';
import LegalInProcessDetailsEditPanel from './LegalInProcessDetailsEditPanel';
import SpanWithLabel from '../../common/SpanWithLabel';
import StackColumn from '../../common/StackColumn';
import SafeLink from '../../common/SafeLink';
import FeatureFlagged from '../../common/featureFlagged/featureFlagged';
import getColumns from '../shared/EntityTerminationActionItemListPage.config';
import { GET_COMPANY_OTHER_DOCUMENTS } from '../../../utils/statApi/CompaniesApi';
import { IDocument } from '../../../utils/statApi/DocumentApi';
import DocumentView from '../../common/documents/DocumentView';
import {
  EntityTerminationUriSegment,
  IEntityTerminationActionItem,
  ILegalInProcessDetails,
} from '../types';
import {
  GET_LEGAL_IN_PROCESS_REVIEW,
  GET_ENTITY_TERMINATION_LEGAL_REVIEW,
  APPROVE_ENTITY_TERMINATION_LEGAL_REVIEW,
  CREATE_ENTITY_TERMINATION_LEGAL_REVIEW_COMMENT,
  ATTACH_ENTITY_TERMINATION_LEGAL_REVIEW_DOCUMENT,
  CREATE_LEGAL_IN_PROCESS_ACTION_ITEM,
  UPDATE_LEGAL_IN_PROCESS_ACTION_ITEM,
  UPDATE_ENTITY_TERMINATION_LEGAL_REVIEW_DOCUMENTS,
} from '../../../utils/statApi/EntityTerminationApi';
import DocumentsPanel from '../../common/documents/DocumentsPanel';
import EntityTerminationActionItemEditPanel from '../actionItems/EntityTerminationActionItemEditPanel';
import ReviewComponent from '../../reviews/ReviewComponent';
import MoveToBlockerPanel from './MoveToBlockerPanel';
import MoveToNoticesPendingPanel from './MoveToNoticesPendingPanel';
import ApproveLegalReviewPanel from './ApproveLegalReviewPanel';
import { ENTITY_STATUS_NOTICES_PENDING } from '../../../app/common/constants/EntityStatusConstants';

interface IEntityTerminationLegalInProcessPageProps {
  loading?: boolean;
  companyCode: string;
  details?: ILegalInProcessDetails;
}

const useStyles = makeStyles({
  actionItemsWrapper: { width: '500px', height: '200px' },
  bottomSpacing: { marginBottom: '50px' },
  actionItemCard: { padding: '0px 20px 0px 20px' },
});

const EntityTerminationLegalInProcessPage = (
  props: IEntityTerminationLegalInProcessPageProps,
): JSX.Element => {
  const { loading, companyCode, details } = props;

  const classes = useStyles();
  const companyCodeId = parseInt(companyCode, 10);

  const {
    data: documentsData,
    loading: documentsLoading,
    refetch: refetchDocuments,
  } = useQuery(GET_COMPANY_OTHER_DOCUMENTS, {
    variables: {
      parentId: companyCodeId,
      showDeletedFiles: false,
    },
    fetchPolicy: 'network-only',
  });

  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState<boolean>(false);
  const [isNewActionItem, setIsNewActionItem] = useState<boolean>(false);
  const [selectedActionItem, setSelectedActionItem] = useState<
    IEntityTerminationActionItem | undefined
  >(undefined);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState<boolean>(false);
  const [isMoveToBlockerPanelOpen, setIsMoveToBlockerPanelOpen] = useState<boolean>(false);
  const [isMoveToNoticesPendingPanelOpen, setIsMoveToNoticesPendingPanelOpen] =
    useState<boolean>(false);
  const [isApproveLegalReviewPanelOpen, setIsApproveLegalReviewPanelOpen] =
    useState<boolean>(false);

  const legalInProcessReview = useQuery(GET_LEGAL_IN_PROCESS_REVIEW, {
    variables: {
      companyCode,
    },
    fetchPolicy: 'cache-and-network',
  });

  const legalReview = useQuery(GET_ENTITY_TERMINATION_LEGAL_REVIEW, {
    variables: {
      companyCode,
    },
    fetchPolicy: 'cache-and-network',
  });

  const legalInProcessData = legalInProcessReview?.data;

  const isNoticesPending =
    legalReview.data?.entityTerminationLegalReview?.entityStatus?.fieldName ===
    ENTITY_STATUS_NOTICES_PENDING;

  const actionItems: IEntityTerminationActionItem[] =
    legalInProcessData?.legalInProcessReview?.actionItems || [];

  const allActionItemsCompleted = actionItems.every(
    (item: IEntityTerminationActionItem) =>
      item.dateCompleted !== null &&
      (item.tasks || []).every((task) => task.completedDate !== null && task.completedDate),
  );

  const hasUploadedDocument = (
    documentsData?.otherCompanyDocuments?.terminationLegalReviewDocuments || []
  ).some((doc: IDocument) => !doc.isDeleted);

  const gatesBlocking = !allActionItemsCompleted || !hasUploadedDocument;
  const approveButtonDisabled = !isNoticesPending || gatesBlocking;

  const approveDisabledTooltip = gatesBlocking
    ? 'All action items and tasks must be completed and at least one document must be uploaded before this review can be approved.'
    : undefined;

  const actionItemCardClassName = classes.actionItemCard;

  const actionItemsCard = (): JSX.Element => {
    return (
      <Card title="Action Item Progress" isLoading={!!loading} headingLevel={2}>
        <Stack horizontal horizontalAlign="space-between" className={actionItemCardClassName}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Display>
              {legalInProcessData?.legalInProcessReview?.actionItems.filter(
                (item: IEntityTerminationActionItem) => item.dateCompleted === null,
              ).length || 0}
            </Display>
            <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 0 }}>
              <Subtitle1>Open</Subtitle1>
            </Stack>
          </Stack>

          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Display>
              {legalInProcessData?.legalInProcessReview?.actionItems.filter(
                (item: IEntityTerminationActionItem) => item.dateCompleted !== null,
              ).length || 0}
            </Display>
            <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 0 }}>
              <Subtitle1>Completed</Subtitle1>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    );
  };

  const newActionButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="LegalInProcess:Edit">
        <>
          <Button
            appearance="primary"
            onClick={() => {
              setIsNewActionItem(true);
              setIsPanelOpen(true);
            }}
          >
            Add New Action Item
          </Button>
        </>
      </CanAccess>
    );
  };

  const renderDetailsActionButton = (): JSX.Element => {
    return (
      <>
        <CanAccess requestedAction="LegalInProcess:Edit">
          <Button
            appearance="subtle"
            title="Edit Details"
            aria-label="Edit Details"
            icon={<EditRegular color={iconButtonStyles.color} />}
            onClick={(): void => setIsDetailsPanelOpen(true)}
          />
        </CanAccess>
      </>
    );
  };

  const renderDocumentsActionButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="EntityTerminationDocuments:Upload">
        <Button
          appearance="subtle"
          title="Upload Documents"
          aria-label="Upload Documents"
          icon={<EditRegular color={iconButtonStyles.color} />}
          onClick={(): void => setIsDocumentPanelOpen(true)}
        />
      </CanAccess>
    );
  };

  const onPanelClose = () => {
    setIsPanelOpen(false);
    setIsNewActionItem(false);
    setSelectedActionItem(undefined);
  };

  const onDocumentPanelClose = () => {
    refetchDocuments();
    setIsDocumentPanelOpen(false);
  };

  const terminationLegalReviewDocuments =
    documentsData?.otherCompanyDocuments?.terminationLegalReviewDocuments || [];

  return (
    <FeatureFlagged flagName="EntityTerminationLegalInProcess">
      <div className={`${bodyContentContainer} lightBackground`}>
        {/* TODO: Add LoadingErrorMessage once you get a query built */}

        <Stack>
          <Stack className={classes.actionItemsWrapper}>{actionItemsCard()}</Stack>

          <Stack
            horizontal
            wrap
            horizontalAlign="space-between"
            tokens={{ childrenGap: 20 }}
            className={classes.bottomSpacing}
          >
            <StackColumn>
              <Card
                title="Details"
                isLoading={!!loading}
                headingLevel={2}
                actionButton={renderDetailsActionButton()}
              >
                <Stack>
                  <StackColumn>
                    <SpanWithLabel labelText="Accounting Plan Link:">
                      <SafeLink
                        url={details?.accountingPlanLink}
                        title={details?.accountingPlanLink}
                        style={truncatedLinkStyle}
                        aria-label={`Accounting Plan link ${details?.accountingPlanLink}`}
                      />
                    </SpanWithLabel>
                  </StackColumn>
                </Stack>
              </Card>
            </StackColumn>
            <StackColumn>
              <Card
                title="Documents"
                isLoading={!!loading || documentsLoading}
                headingLevel={2}
                actionButton={renderDocumentsActionButton()}
              >
                {!documentsLoading && terminationLegalReviewDocuments.length > 0 ? (
                  <Stack horizontal wrap tokens={{ childrenGap: 20 }}>
                    {terminationLegalReviewDocuments
                      .filter((doc: IDocument) => !doc.isDeleted)
                      .map((document: IDocument, index: number) => (
                        <DocumentView
                          key={document.id || index}
                          document={document}
                          index={index}
                        />
                      ))}
                  </Stack>
                ) : (
                  !documentsLoading && <p>No Documents Uploaded</p>
                )}
              </Card>
            </StackColumn>
          </Stack>

          <Stack>
            <Card
              title="Action Items"
              isLoading={!!loading}
              headingLevel={2}
              actionButton={newActionButton()}
            >
              <ShimmeredDetailsListWrapper
                columns={getColumns(companyCode, EntityTerminationUriSegment.LegalInProcess)}
                items={legalInProcessData?.legalInProcessReview?.actionItems || []}
                singularListLabel="Action Items"
                pluralListLabel="Action Items"
              />
            </Card>
          </Stack>

          <Stack>
            <ReviewComponent
              review={legalReview.data?.entityTerminationLegalReview?.review}
              approveMutation={APPROVE_ENTITY_TERMINATION_LEGAL_REVIEW}
              loading={legalReview.loading}
              createCommentMutation={CREATE_ENTITY_TERMINATION_LEGAL_REVIEW_COMMENT}
              attachDocumentMutation={ATTACH_ENTITY_TERMINATION_LEGAL_REVIEW_DOCUMENT}
              documentAccessPolicy="EntityTermination:ReviewContribute"
              commentAccessPolicy="EntityTermination:ReviewContribute"
              displayOnly={!legalReview.data?.entityTerminationLegalReview?.hasActiveLegalReview}
              customActionButtons={[
                {
                  label: 'Approve',
                  accessPolicy: 'EntityTerminationLegalReview:Approval',
                  appearance: 'primary',
                  disabled: approveButtonDisabled,
                  tooltip: approveDisabledTooltip,
                  onClick: () => setIsApproveLegalReviewPanelOpen(true),
                },
                {
                  label: 'Blocker',
                  accessPolicy: 'EntityTerminationLegalReview:Approval',
                  appearance: 'secondary',
                  disabled: isNoticesPending,
                  onClick: () => setIsMoveToBlockerPanelOpen(true),
                },
                {
                  label: 'Notices Pending',
                  accessPolicy: 'EntityTerminationLegalReview:Approval',
                  appearance: 'secondary',
                  disabled: isNoticesPending,
                  onClick: () => setIsMoveToNoticesPendingPanelOpen(true),
                },
              ]}
            />
            {isMoveToBlockerPanelOpen && (
              <MoveToBlockerPanel
                companyCode={companyCode}
                closePanel={() => setIsMoveToBlockerPanelOpen(false)}
              />
            )}
            {isMoveToNoticesPendingPanelOpen && (
              <MoveToNoticesPendingPanel
                companyCode={companyCode}
                closePanel={() => setIsMoveToNoticesPendingPanelOpen(false)}
              />
            )}
            {isApproveLegalReviewPanelOpen && legalReview.data?.entityTerminationLegalReview && (
              <ApproveLegalReviewPanel
                companyCode={companyCode}
                closePanel={() => setIsApproveLegalReviewPanelOpen(false)}
                data={legalReview.data.entityTerminationLegalReview}
              />
            )}
          </Stack>
        </Stack>

        {isPanelOpen && (
          <EntityTerminationActionItemEditPanel
            closePanel={onPanelClose}
            isNewActionItem={isNewActionItem}
            selectedActionItem={selectedActionItem}
            companyCode={companyCode}
            refetchQueries={[
              {
                query: GET_LEGAL_IN_PROCESS_REVIEW,
                variables: { companyCode },
              },
            ]}
            updateMutation={UPDATE_LEGAL_IN_PROCESS_ACTION_ITEM}
            createMutation={CREATE_LEGAL_IN_PROCESS_ACTION_ITEM}
            dtoVariableName="legalInProcessActionItemDto"
          />
        )}
        {isDocumentPanelOpen && (
          <DocumentsPanel
            closePanel={onDocumentPanelClose}
            parentId={companyCodeId}
            documentType="TerminationLegalReview"
            initialDocuments={
              documentsData?.otherCompanyDocuments?.terminationLegalReviewDocuments || []
            }
            mutation={UPDATE_ENTITY_TERMINATION_LEGAL_REVIEW_DOCUMENTS}
            showDeletedFiles={false}
            panelTitle="Termination Legal Review"
            documentTypeIsServerDefined
          />
        )}

        {isDetailsPanelOpen && (
          <LegalInProcessDetailsEditPanel
            closePanel={() => setIsDetailsPanelOpen(false)}
            companyCode={companyCode}
            details={{
              accountingPlanLink: details?.accountingPlanLink,
            }}
          />
        )}
      </div>
    </FeatureFlagged>
  );
};

export default EntityTerminationLegalInProcessPage;
